<template>
	<section
		class="markets-deep-dives"
		id="deep-dives"
	>
		<Subtitle
			weight="medium"
			class="mb-4 title-font"
		>
			Deep Dives & Analysis
		</Subtitle>
		<!-- Desktop View -->
		<div v-if="breakpoints.isGreaterOrEqual('lg')">
			<!-- Loading State for Hero Image -->
			<div
				v-if="loading"
				class="w-full h-[326px] rounded-xl bg-slate-300/[0.1] animate-pulse mb-4"
			></div>

			<!-- Hero Image -->
			<NuxtLink
				v-else-if="dives && dives.length > 0 && dives[0]?.image"
				class="flex items-center gap-4 mb-4"
				:to="dives[0].uri || `/${dives[0].slug}/`"
			>
				<img
					:src="`https://content.stockstotrade.com/cdn-cgi/image/quality=100,format=webp,width=700/${dives[0].image}`"
					:alt="dives[0].title || 'Deep Dives & Analysis'"
					class="w-full h-[326px] object-cover rounded-xl bg-slate-300/[0.1]"
					loading="lazy"
					height="326"
					width="454"
					@error="onImageError"
				/>
			</NuxtLink>

			<!-- Fallback when no image -->
			<div
				v-else-if="!loading"
				class="w-full h-48 rounded-xl bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center mb-4"
			>
				<span class="text-gray-600 font-medium">Deep Dives & Analysis</span>
			</div>
		</div>

		<!-- Loading State for Desktop List -->
		<div
			v-if="breakpoints.isGreaterOrEqual('lg') && loading"
			class="space-y-4"
		>
			<div
				v-for="n in 3"
				:key="n"
				class="py-4 border-b border-slate-200 animate-pulse"
			>
				<div class="h-6 bg-slate-300/[0.1] rounded mb-2 w-3/4"></div>
				<div class="h-4 bg-slate-300/[0.1] rounded mb-3 w-full"></div>
			</div>
		</div>

		<!-- Content for Desktop -->
		<ul v-if="breakpoints.isGreaterOrEqual('lg') && !loading && dives.length > 0">
			<li
				v-for="(item, idx) in dives"
				:key="item.id || idx"
				class="py-4 border-b border-slate-200"
			>
				<NuxtLink
					:to="item.uri || `/${item.slug}/`"
					class="block hover:bg-gray-50 transition-colors"
				>
					<h3
						class="text-xl font-bold mb-1 text-black line-clamp-3 hover:underline"
						v-html="item.title"
					/>

					<span
						class="text-base text-[#444] mb-3 line-clamp-2"
						v-html="item.content"
					/>

					<!-- <div
						v-if="item.stock_pairs && item.stock_pairs.length > 0"
						class="flex gap-2 mb-2"
					>
						<template
							v-for="ticker in item.stock_pairs.slice(0, 3)"
							:key="ticker"
						>
							<TickerBadge
								:symbol="ticker.toUpperCase()"
								:change="generateMockPercentage(ticker)"
							/>
						</template>
					</div> -->
				</NuxtLink>
			</li>
		</ul>

		<!-- Empty State for Desktop -->
		<div
			v-if="breakpoints.isGreaterOrEqual('lg') && !loading && dives.length === 0"
			class="py-8 text-center"
		>
			<p class="text-gray-500">No analysis posts available at the moment.</p>
		</div>
		<!-- Mobile View -->
		<div
			v-else
			class="relative"
		>
			<!-- Loading State for Mobile -->
			<div
				v-if="loading"
				class="flex overflow-x-auto hide-scrollbar pb-4 -mb-4"
			>
				<div
					v-for="n in 3"
					:key="n"
					class="flex-shrink-0 w-[80vw] pr-5 animate-pulse"
				>
					<div class="h-6 bg-slate-300/[0.1] rounded mb-2 w-3/4"></div>
					<div class="h-4 bg-slate-300/[0.1] rounded mb-3 w-full"></div>
					<div class="flex gap-2">
						<div class="h-6 bg-slate-300/[0.1] rounded w-16"></div>
						<div class="h-6 bg-slate-300/[0.1] rounded w-16"></div>
					</div>
				</div>
			</div>

			<!-- Content for Mobile -->
			<div
				v-if="breakpoints.isSmaller('lg') && !loading && dives.length > 0"
				class="flex lg:gap-4 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-4 -mb-4 scroll-smooth lg"
				ref="scrollContainer"
			>
				<NuxtLink
					v-for="(item, idx) in mobileDisplayPosts"
					:key="item.id || idx"
					class="flex-shrink-0 w-[80vw] lg:w-[85vw] lg:py-4 snap-center lg:px-0 border-r border-slate-200 lg:border-none"
					:class="{
						'px-5': idx != 0,
						'pr-5': idx == 0
					}"
					:to="item.uri || `/${item.slug}/`"
				>
					<h3
						class="text-xl font-bold mb-1 text-black hover:underline"
						v-html="item.title"
					/>

					<div
						class="text-base text-[#444] mb-3"
						v-html="item.content"
					/>
					<!-- <div
						v-if="item.stock_pairs && item.stock_pairs.length > 0"
						class="flex gap-2 lg:mb-2"
					>
						<template
							v-for="ticker in item.stock_pairs.slice(0, 2)"
							:key="ticker"
						>
							<TickerBadge
								:symbol="ticker.toUpperCase()"
								:change="generateMockPercentage(ticker)"
							/>
						</template>
					</div> -->
				</NuxtLink>
			</div>

			<!-- Empty State for Mobile -->
			<div
				v-if="!loading && dives.length === 0"
				class="py-8 text-center"
			>
				<p class="text-gray-500">No analysis posts available at the moment.</p>
			</div>

			<!-- Navigation Arrows - Mobile Only -->
			<div
				v-if="!loading && mobileDisplayPosts.length > 1"
				class="flex gap-3 mt-4 lg:hidden"
			>
				<button
					:disabled="activeIndex === 0"
					:class="[
						'flex items-center justify-center text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
						activeIndex === 0
							? 'opacity-50 cursor-not-allowed'
							: 'hover:scale-105 active:scale-95'
					]"
					@click="goToPrevious"
					style="
						box-sizing: border-box;
						padding: 9px;
						gap: 4.5px;
						width: 36px;
						height: 36px;
						background: linear-gradient(90deg, #50b5ff 0%, #be55ff 99.95%);
						border: 0.5625px solid #ffffff;
						border-radius: 4px;
						flex: none;
						order: 0;
						flex-grow: 0;
					"
				>
					<svg
						width="18"
						height="18"
						viewBox="0 0 18 18"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M11.25 13.5L6.75 9L11.25 4.5"
							stroke="currentColor"
							stroke-width="1.125"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
				<button
					:disabled="activeIndex === mobileDisplayPosts.length - 1"
					:class="[
						'flex items-center justify-center text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
						activeIndex === mobileDisplayPosts.length - 1
							? 'opacity-50 cursor-not-allowed'
							: 'hover:scale-105 active:scale-95'
					]"
					@click="goToNext"
					style="
						box-sizing: border-box;
						padding: 9px;
						gap: 4.5px;
						width: 36px;
						height: 36px;
						background: linear-gradient(90deg, #50b5ff 0%, #be55ff 99.95%);
						border: 0.5625px solid #ffffff;
						border-radius: 4px;
						flex: none;
						order: 0;
						flex-grow: 0;
					"
				>
					<svg
						width="18"
						height="18"
						viewBox="0 0 18 18"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M6.75 13.5L11.25 9L6.75 4.5"
							stroke="currentColor"
							stroke-width="1.125"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
			</div>
		</div>
	</section>
</template>
<script setup lang="ts">
import { breakpointsTailwind } from "@vueuse/core";
import { ref, computed, onMounted, onUnmounted } from "vue";
import TickerBadge from "./TickerBadge.vue";

const breakpoints = useBreakpoints(breakpointsTailwind);
const scrollContainer = ref(null);
const activeIndex = ref(0);

// Fetch analysis data server-side so the section renders on first paint.
const { data: deepDivesData, error: deepDivesError } = await useAsyncData(
	"markets-deep-dives",
	async () => {
		try {
			const response = await $fetch("/api/analysis-deep-dives");
			if ((response as any).success === false) return [];
			const recs = (response as any).recommendations;
			return Array.isArray(recs) ? recs.slice(0, 3) : [];
		} catch {
			return [];
		}
	}
);

const dives = computed(() => deepDivesData.value ?? []);
const loading = ref(false);
const error = ref(deepDivesError.value?.message ?? null);

// Computed property for mobile display posts (max 3)
const mobileDisplayPosts = computed(() => {
	return dives.value.slice(0, 3);
});

// Generate consistent mock percentage for stock tickers
const generateMockPercentage = (ticker) => {
	const seed = ticker
		.split("")
		.reduce((acc, char) => acc + char.charCodeAt(0), 0);
	const random = Math.sin(seed) * 10000;
	const normalizedRandom = (random - Math.floor(random)) * 20 - 5; // Range: -5% to +15%
	const sign = normalizedRandom >= 0 ? "+" : "";
	return `${sign}${normalizedRandom.toFixed(2)}%`;
};

// Format title to be more readable
const formatTitle = (title) => {
	if (!title) {
		return "";
	}
	return title
		.replace(/-/g, " ")
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ")
		.replace(/Ai /g, "AI ")
		.replace(/Ai$/g, "AI");
};

// Strip HTML and limit content length (SSR-safe: avoids document.createElement on server)
const formatContent = (content) => {
	if (!content) return "";
	const text = content.replace(/<[^>]*>/g, "").trim();
	return text.length > 120 ? text.substring(0, 120) + "..." : text;
};

// Handle image loading errors
const onImageError = (event) => {
	const img = event.target;
	img.style.display = "none";
	// You could set a fallback image here if needed
};

// Validate and sanitize API data
const validatePostData = (post) => {
	return {
		id: post.id || Math.random().toString(36),
		title: post.title || "Untitled Analysis",
		content: post.content || "",
		image: post.image || "",
		slug: post.slug || "",
		uri: post.uri || `/${post.slug || "analysis"}/`,
		stock_pairs: Array.isArray(post.stock_pairs) ? post.stock_pairs : [],
		date: post.date || new Date().toISOString(),
		type: post.type || "post"
	};
};

// Navigation functions
const goToNext = () => {
	if (activeIndex.value < mobileDisplayPosts.value.length - 1) {
		activeIndex.value++;
		scrollToActiveItem();
	}
};

const goToPrevious = () => {
	if (activeIndex.value > 0) {
		activeIndex.value--;
		scrollToActiveItem();
	}
};

const scrollToActiveItem = () => {
	if (scrollContainer.value && mobileDisplayPosts.value.length > 0) {
		const itemWidth =
			scrollContainer.value.scrollWidth / mobileDisplayPosts.value.length;
		const scrollPosition = activeIndex.value * itemWidth;
		scrollContainer.value.scrollTo({
			left: scrollPosition,
			behavior: "smooth"
		});
	}
};

// Auto-detect active item based on scroll position
const handleScroll = () => {
	if (scrollContainer.value && mobileDisplayPosts.value.length > 0) {
		const scrollLeft = scrollContainer.value.scrollLeft;
		const itemWidth =
			scrollContainer.value.scrollWidth / mobileDisplayPosts.value.length;
		const newActiveIndex = Math.round(scrollLeft / itemWidth);

		if (
			newActiveIndex !== activeIndex.value &&
			newActiveIndex >= 0 &&
			newActiveIndex < mobileDisplayPosts.value.length
		) {
			activeIndex.value = newActiveIndex;
		}
	}
};

onMounted(() => {
	if (scrollContainer.value) {
		scrollContainer.value.addEventListener("scroll", handleScroll);
	}
});

onUnmounted(() => {
	if (scrollContainer.value) {
		scrollContainer.value.removeEventListener("scroll", handleScroll);
	}
});
</script>
<style scoped>
.hide-scrollbar {
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
}
.hide-scrollbar::-webkit-scrollbar {
	display: none; /* Chrome, Safari, and Opera */
}

/* Smooth scrolling for the carousel */
.scroll-smooth {
	scroll-behavior: smooth;
}

/* Navigation button states */
button:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}

button:not(:disabled):hover {
	transform: translateY(-1px);
}

button:not(:disabled):active {
	transform: scale(0.95);
}

/* Mobile-specific improvements */
@media (max-width: 1023px) {
	.snap-center {
		scroll-snap-align: center;
	}

	.snap-x {
		scroll-snap-type: x mandatory;
	}
}
</style>
